import {
  MessageCircleX,
  Loader2,
  CheckCircle,
  XCircle,
  Bot,
  Eye,
} from 'lucide-react';
import { FC, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { map } from 'lodash';
import AnalyticsService from '../../../../services/analytics.service';
import {
  TUserQuizData,
  TUserQuizAttemptSummary,
  TQuizResponseDetail,
} from '../../../../types/analytics.types';
import Button from '../../../common/Button';
import { ButtonSize, ButtonVariant } from '../../../../types/button.enum';

interface StudentQuizDataProps {
  userId: string;
  studentName?: string;
  onClose?: () => void;
}

const thStyle =
  'p-3 min-w-[100px] sticky top-0 bg-[white] text-appTextLight font-medium z-[1]';

const getLocaleDateTime = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/** Badge for AI Assist Level */
const AiAssistBadge: FC<{ level: string; percentage: number }> = ({
  level,
  percentage,
}) => {
  const colorMap: Record<string, string> = {
    Low: 'bg-green-50 text-green-700',
    Medium: 'bg-yellow-50 text-yellow-700',
    High: 'bg-red-50 text-red-700',
  };
  return (
    <span
      className={`px-2 py-1 rounded-lg text-[12px] font-medium ${colorMap[level] || 'bg-gray-100 text-gray-700'}`}
    >
      {level} ({percentage}%)
    </span>
  );
};

/** Badge for Completion status */
const CompletionBadge: FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string }> = {
    'on-time': { label: 'On-time', cls: 'bg-green-50 text-green-700' },
    late: { label: 'Late', cls: 'bg-red-50 text-red-700' },
    'no-deadline': { label: 'No deadline', cls: 'bg-gray-100 text-gray-600' },
  };
  const info = map[status] || map['no-deadline'];
  return (
    <span
      className={`px-2 py-1 rounded-lg text-[12px] font-medium ${info.cls}`}
    >
      {info.label}
    </span>
  );
};

/** Dialog to show AI questions the student asked */
const AiQuestionsDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  questions: string[];
  questionText: string;
}> = ({ isOpen, onClose, questions, questionText }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-[#E2E8F0]">
          <h3 className="text-[16px] font-semibold text-[#020817]">
            AI Questions Asked
          </h3>
          <button
            onClick={onClose}
            className="text-appTextLight hover:text-[#020817] transition-colors text-xl"
          >
            ✕
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          <p className="text-[13px] text-appTextLight mb-3">
            For: <span className="font-medium text-[#333]">{questionText}</span>
          </p>
          <div className="space-y-2">
            {questions.map((q, i) => (
              <div
                key={i}
                className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-[14px] text-[#333]"
              >
                <span className="text-blue-600 font-medium mr-2">Q{i + 1}:</span>
                {q}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentQuizData: FC<StudentQuizDataProps> = ({
  userId,
  studentName,
  onClose,
}) => {
  const { quizId } = useParams();
  const [userData, setUserData] = useState<TUserQuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] =
    useState<TUserQuizAttemptSummary | null>(null);
  const [aiDialog, setAiDialog] = useState<{
    open: boolean;
    questions: string[];
    questionText: string;
  }>({ open: false, questions: [], questionText: '' });

  const fetchData = useCallback(async () => {
    if (!quizId || !userId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await AnalyticsService.fetchUserQuizData({
        quizId,
        userId,
      });
      if (response.data.data) {
        setUserData(response.data.data);
        // Auto-select latest attempt
        if (response.data.data.attempts?.length > 0) {
          setSelectedAttempt(response.data.data.attempts[0]);
        }
      } else {
        setError(response.data.message || 'No data found for this user.');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching user data.');
      console.warn('Error fetching student quiz data: ', err);
    } finally {
      setLoading(false);
    }
  }, [quizId, userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="mt-4 w-full flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-gray-400" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-lg p-4">
        <p className="text-red-700 text-[14px] font-medium">{error}</p>
      </div>
    );
  }

  const { latestInsight, attempts } = userData || {};
  const displayName = userData?.studentName || studentName || 'Student';
  const displayEmail = userData?.email || '';

  return (
    <div className="w-full pb-5">
      {/* Header with student info */}
      <div className="mb-4 p-4 bg-white border border-[#E2E8F0] rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-[18px] font-semibold text-[#020817]">
              {displayName}
            </h2>
            {displayEmail && (
              <p className="text-[14px] text-appTextLight">{displayEmail}</p>
            )}
            <p className="text-[13px] text-appTextLight mt-1">
              Total Attempts: {userData?.totalAttempts ?? 0}
            </p>
          </div>
          {onClose && (
            <Button
              size={ButtonSize.SMALL}
              variant={ButtonVariant.SECONDARY}
              onClick={onClose}
              // className="text-appTextLight hover:text-[#020817] transition-colors"
            >
              Back
            </Button>
          )}
        </div>
      </div>

      {/* Insights Section - Progress Bars */}
      {latestInsight && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Skills Progress */}
            <div className="border border-[#E2E8F0] bg-white rounded-lg p-4">
              <div className="font-medium text-[16px] mb-4">
                Skills Progress
              </div>
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {latestInsight.skills && latestInsight.skills.length > 0 ? (
                  map(latestInsight.skills, (skill, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[14px] font-medium text-[#333] truncate max-w-[70%]">
                          {skill.name}
                        </span>
                        <span className="text-[14px] font-semibold text-[#8784d4]">
                          {skill.completion}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-300"
                          style={{
                            width: `${skill.completion}%`,
                            backgroundColor:
                              skill.completion >= 70
                                ? '#10b981'
                                : skill.completion >= 50
                                  ? '#f59e0b'
                                  : '#ef4444',
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col justify-center items-center py-10">
                    <MessageCircleX color="#999" size={40} />
                    <p className="text-[14px] font-medium text-appTextLight mt-3">
                      No skills data found.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Learning Outcomes Progress */}
            <div className="border border-[#E2E8F0] bg-white rounded-lg p-4">
              <div className="font-medium text-[16px] mb-4">
                Learning Outcomes
              </div>
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {latestInsight.learningOutcomes &&
                latestInsight.learningOutcomes.length > 0 ? (
                  map(latestInsight.learningOutcomes, (outcome, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[14px] font-medium text-[#333] truncate max-w-[70%]">
                          {outcome.name}
                        </span>
                        <span className="text-[14px] font-semibold text-[#91c89e]">
                          {outcome.completion}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-300"
                          style={{
                            width: `${outcome.completion}%`,
                            backgroundColor:
                              outcome.completion >= 70
                                ? '#10b981'
                                : outcome.completion >= 50
                                  ? '#f59e0b'
                                  : '#ef4444',
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col justify-center items-center py-10">
                    <MessageCircleX color="#999" size={40} />
                    <p className="text-[14px] font-medium text-appTextLight mt-3">
                      No learning outcomes data found.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Strengths and Areas for Improvement */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Student Strengths */}
            <div className="border border-[#E2E8F0] bg-white rounded-lg p-4">
              <div className="font-medium text-[16px] mb-4 flex items-center gap-2">
                <span className="text-green-600">✓</span>
                Student Strengths
              </div>
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {latestInsight.strengths &&
                latestInsight.strengths.length > 0 ? (
                  map(latestInsight.strengths, (skill, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[14px] font-medium text-[#333] truncate max-w-[70%]">
                          {skill.name}
                        </span>
                        <span className="text-[14px] font-semibold text-green-600">
                          {skill.completion}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-300 bg-green-500"
                          style={{
                            width: `${skill.completion}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col justify-center items-center py-10">
                    <MessageCircleX color="#999" size={40} />
                    <p className="text-[14px] font-medium text-appTextLight mt-3">
                      No strengths identified yet.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Areas for Improvement */}
            <div className="border border-[#E2E8F0] bg-white rounded-lg p-4">
              <div className="font-medium text-[16px] mb-4 flex items-center gap-2">
                <span className="text-orange-600">⚠</span>
                Areas for Improvement
              </div>
              <div className="space-y-4 max-h-[300px] overflow-y-auto">
                {latestInsight.areasForImprovement &&
                latestInsight.areasForImprovement.length > 0 ? (
                  map(latestInsight.areasForImprovement, (area, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-[14px] font-medium text-[#333] truncate max-w-[70%]">
                          {area.name}
                        </span>
                        <span className="text-[14px] font-semibold text-orange-600">
                          {area.completion}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-300"
                          style={{
                            width: `${area.completion}%`,
                            backgroundColor:
                              area.completion >= 50
                                ? '#f59e0b'
                                : '#ef4444',
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col justify-center items-center py-10">
                    <MessageCircleX color="#999" size={40} />
                    <p className="text-[14px] font-medium text-appTextLight mt-3">
                      No improvement areas identified.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* No insights fallback */}
      {!latestInsight && (
        <div className="mb-4 p-6 bg-white border border-[#E2E8F0] rounded-lg text-center">
          <MessageCircleX color="#999" size={40} className="mx-auto" />
          <p className="text-[14px] font-medium text-appTextLight mt-3">
            No AI insights generated yet for this student.
          </p>
        </div>
      )}

      {/* Attempt Selector */}
      {attempts && attempts.length > 1 && (
        <div className="mb-4 flex items-center gap-3">
          <label className="text-[14px] font-medium text-[#333]">
            Select Attempt:
          </label>
          <select
            className="border border-[#E2E8F0] rounded-lg px-3 py-2 text-[14px] bg-white"
            value={selectedAttempt?.attemptId || ''}
            onChange={(e) => {
              const attempt = attempts.find(
                (a) => a.attemptId === e.target.value
              );
              setSelectedAttempt(attempt || null);
            }}
          >
            {attempts.map((attempt, idx) => (
              <option key={attempt.attemptId} value={attempt.attemptId}>
                Attempt {idx + 1} — Score: {attempt.score}% —{' '}
                {getLocaleDateTime(attempt.completedAt)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Student Performance Summary */}
      {selectedAttempt && (
        <div className="mb-4">
          <div className="font-medium text-[16px] mb-3">
            Student Performance Summary
          </div>
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center">
              <p className="text-[12px] text-appTextLight mb-1">Score</p>
              <p
                className={`text-[24px] font-bold ${
                  selectedAttempt.performanceSummary.score >= 70
                    ? 'text-green-600'
                    : selectedAttempt.performanceSummary.score >= 50
                      ? 'text-orange-600'
                      : 'text-red-600'
                }`}
              >
                {selectedAttempt.performanceSummary.score}%
              </p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center">
              <p className="text-[12px] text-appTextLight mb-1">Attempt</p>
              <p className="text-[24px] font-bold text-[#020817]">
                {selectedAttempt.performanceSummary.attemptNumber}
                <span className="text-[14px] text-appTextLight font-normal">
                  {' '}
                  of {selectedAttempt.performanceSummary.totalAllowedAttempts}
                </span>
              </p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center">
              <p className="text-[12px] text-appTextLight mb-1">Time Taken</p>
              <p className="text-[20px] font-bold text-[#020817]">
                {selectedAttempt.performanceSummary.formattedTime}
              </p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center">
              <p className="text-[12px] text-appTextLight mb-1">Completion</p>
              <div className="mt-2">
                <CompletionBadge
                  status={selectedAttempt.performanceSummary.completion}
                />
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 text-center">
              <p className="text-[12px] text-appTextLight mb-1">AI Assist</p>
              <div className="mt-2">
                <AiAssistBadge
                  level={selectedAttempt.performanceSummary.aiAssistLevel}
                  percentage={
                    selectedAttempt.performanceSummary.aiAssistPercentage
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question Level Breakdown */}
      {selectedAttempt && (
        <div className="w-full overflow-x-auto bg-white border border-[#E2E8F0] rounded-lg">
          <div className="font-medium text-[16px] p-4 mb-2 border-b-appLightBlue border-b">
            Question Level Breakdown (
            {selectedAttempt.quizResponse?.length || 0} questions)
          </div>
          <div className="overflow-y-auto max-h-[400px]">
            <table className="text-[14px] bg-white min-w-full">
              <thead>
                <tr className="text-[#616161] text-left">
                  <th className={thStyle}>#</th>
                  <th className={thStyle}>Question</th>
                  <th className={thStyle}>Student Answer</th>
                  <th className={thStyle}>Result</th>
                  <th className={thStyle}>AI Assist</th>
                </tr>
              </thead>
              <tbody className="text-[#333] font-regular">
                {selectedAttempt.quizResponse &&
                selectedAttempt.quizResponse.length > 0 ? (
                  map(
                    selectedAttempt.quizResponse,
                    (resp: TQuizResponseDetail, idx: number) => (
                      <tr
                        className="border-b border-[#E2E8F0] hover:bg-gray-50"
                        key={idx}
                      >
                        <td className="px-3 py-2 text-appTextLight">
                          {resp.questionNumber || idx + 1}
                        </td>
                        <td className="px-3 py-2 max-w-[300px]">
                          <div className="truncate" title={resp.question}>
                            {resp.question}
                          </div>
                        </td>
                        <td className="px-3 py-2 max-w-[200px]">
                          <div className="truncate" title={resp.userAnswer}>
                            {resp.userAnswer || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {resp.isCorrect ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle size={16} />
                              <span className="text-[12px] font-medium">
                                Correct
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-600">
                              <XCircle size={16} />
                              <span className="text-[12px] font-medium">
                                Incorrect
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {resp.aiAssist ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1 text-blue-600">
                                <Bot size={16} />
                                <span className="text-[12px] font-medium">
                                  Yes
                                </span>
                              </div>
                              {resp.aiQuestions &&
                                resp.aiQuestions.length > 0 && (
                                  <button
                                    onClick={() =>
                                      setAiDialog({
                                        open: true,
                                        questions: resp.aiQuestions,
                                        questionText: resp.question,
                                      })
                                    }
                                    className="p-1 hover:bg-blue-50 rounded transition-colors"
                                    title="View AI questions"
                                  >
                                    <Eye size={14} className="text-blue-500" />
                                  </button>
                                )}
                            </div>
                          ) : (
                            <span className="text-appTextLight">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td colSpan={6}>
                      <div className="flex flex-col justify-center items-center py-10">
                        <MessageCircleX color="black" size={60} />
                        <p className="text-[14px] font-medium text-[#020817] mt-3">
                          No quiz responses found.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Questions Dialog */}
      <AiQuestionsDialog
        isOpen={aiDialog.open}
        onClose={() =>
          setAiDialog({ open: false, questions: [], questionText: '' })
        }
        questions={aiDialog.questions}
        questionText={aiDialog.questionText}
      />
    </div>
  );
};

export default StudentQuizData;
